import * as Sentry from "@sentry/react";
import { getPostHogSessionUrl } from "@/lib/posthog";

/**
 * Initialize Sentry for error tracking and performance monitoring
 *
 * To enable Sentry in production:
 * 1. Create a Sentry account at https://sentry.io
 * 2. Create a new project for React
 * 3. Get your DSN (Data Source Name)
 * 4. Set VITE_SENTRY_DSN environment variable
 *
 * Example: VITE_SENTRY_DSN=https://xxx@xxx.ingest.sentry.io/xxx
 */
export function initSentry() {
  const sentryDsn = import.meta.env.VITE_SENTRY_DSN;

  // Only initialize if DSN is provided
  if (!sentryDsn) {
    console.log("Sentry DSN not configured. Skipping Sentry initialization.");
    return;
  }

  Sentry.init({
    dsn: sentryDsn,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Performance Monitoring
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0, // 10% in production, 100% in development

    // Session Replay
    replaysSessionSampleRate: 0.1, // 10% of sessions
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors

    // Environment
    environment: import.meta.env.MODE,

    // Release tracking
    release: import.meta.env.VITE_APP_VERSION || "unknown",

    // Filter out known errors
    beforeSend(event, hint) {
      // Filter out network errors from ad blockers
      if (event.exception) {
        const error = hint.originalException;
        if (error && typeof error === "object" && "message" in error) {
          const message = String(error.message);
          if (
            message.includes("Failed to fetch") ||
            message.includes("NetworkError") ||
            message.includes("Load failed")
          ) {
            return null; // Don't send to Sentry
          }
        }
      }
      return event;
    },
  });
}

/**
 * Capture an error to Sentry
 */
export function captureError(error: Error, context?: Record<string, any>) {
  if (context) {
    Sentry.setContext("error_context", context);
  }
  Sentry.captureException(error);
}

/**
 * Capture a message to Sentry
 */
export function captureMessage(
  message: string,
  level: Sentry.SeverityLevel = "info"
) {
  Sentry.captureMessage(message, level);
}

/**
 * Set user context for Sentry — enriched with role, branch name, unit name, position
 * Every Sentry error will show who triggered it and which branch/unit they belong to.
 */
export function setUser(user: {
  id: string;
  email?: string;
  username?: string;
  role?: string;
  name?: string;
  branchId?: number | null;
  position?: string | null;
  branchName?: string | null;
  unitName?: string | null;
} | null) {
  if (!user) {
    Sentry.setUser(null);
    return;
  }

  // Core Sentry user identity
  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.username,
    // segment maps to role in Sentry user context panel
    segment: user.role,
  });

  // Searchable tags — appear on every error event for this user session
  Sentry.setTag("user.role", user.role ?? "unknown");
  Sentry.setTag("user.name", user.name ?? user.username ?? "unknown");
  Sentry.setTag("user.branchId", String(user.branchId ?? "none"));
  Sentry.setTag("user.branchName", user.branchName ?? "unknown");  // e.g. ສາຂາວັງວີງ
  Sentry.setTag("user.unitName", user.unitName ?? "none");          // e.g. ຂະແຫນງສິນເຊື່
  Sentry.setTag("user.position", user.position ?? "none");
  // Link to PostHog session replay — click this in Sentry to watch exactly what the user did
  const sessionUrl = getPostHogSessionUrl();
  if (sessionUrl) Sentry.setTag("posthog.session_replay", sessionUrl);
}

/**
 * Clear user context
 */
export function clearUser() {
  Sentry.setUser(null);
}
